import { useUpdateAppCollectionMutation } from "@/features/auth/api/authAPI";
import React, { useEffect, useRef, useState } from "react";
import styles from "./CollectionDetailView.module.scss";
import { NavLink, useParams } from "react-router-dom";
import { AppCollection } from "@/features/auth/types/auth";

interface CollectionDetailViewHeaderProps {
  collection: AppCollection | null;
  onCollectionUpdate: (updatedCollection: AppCollection) => void;
}

const CollectionDetailViewHeader: React.FC<CollectionDetailViewHeaderProps> = ({
  collection,
  onCollectionUpdate,
}) => {
  const { id } = useParams<{ id: string }>();
  const [updateAppCollection] = useUpdateAppCollectionMutation();
  const [isEditing, setIsEditing] = useState(false);
  const [collectionName, setCollectionName] = useState("");
  const [originalName, setOriginalName] = useState("");
  const titleRef = useRef<HTMLHeadingElement>(null);

  // Update local state when collection prop changes
  useEffect(() => {
    if (collection) {
      setCollectionName(collection.name);
      setOriginalName(collection.name);
    }
  }, [collection]);

  useEffect(() => {
    if (isEditing && titleRef.current) {
      titleRef.current.focus();
      // Select all text
      const range = document.createRange();
      range.selectNodeContents(titleRef.current);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }, [isEditing]);

  const handleTitleClick = () => {
    if (!isEditing) {
      setOriginalName(collectionName);
      setIsEditing(true);
    }
  };

  const handleTitleBlur = async () => {
    if (!titleRef.current) return;

    const newName = titleRef.current.textContent?.trim() || "";

    // If empty, revert to original name
    if (newName === "") {
      setCollectionName(originalName);
      titleRef.current.textContent = originalName;
      setIsEditing(false);
      return;
    }

    // If no change, just exit edit mode without API call
    if (newName === originalName) {
      setIsEditing(false);
      return;
    }

    // Only call API if there's an actual change
    try {
      await updateAppCollection({
        id: Number(id),
        name: newName,
      }).unwrap();

      // Update local state
      setCollectionName(newName);
      setOriginalName(newName);
      setIsEditing(false);

      // Update parent component with new collection data
      if (collection) {
        const updatedCollection = { ...collection, name: newName };
        onCollectionUpdate(updatedCollection);
      }
    } catch (error) {
      console.error("Failed to update collection name:", error);
      setCollectionName(originalName);
      titleRef.current.textContent = originalName;
      setIsEditing(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLHeadingElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      titleRef.current?.blur();
    } else if (e.key === "Escape") {
      e.preventDefault();
      if (titleRef.current) {
        titleRef.current.textContent = originalName;
        setCollectionName(originalName);
        setIsEditing(false);
        titleRef.current.blur();
      }
    }
  };

  if (!collection) {
    return null; // or a loading state
  }

  return (
    <div className={styles.header}>
      <NavLink to="/saved-library/collections/" className={styles.groupArrow}>
        <img src="/img/blackIcon/ChevronLeft.svg" alt="arrowGroupLeftImg" />
      </NavLink>
      <div className={styles.headerTitle}>
        <h2
          ref={titleRef}
          onClick={handleTitleClick}
          onBlur={handleTitleBlur}
          onKeyDown={handleKeyDown}
          contentEditable={isEditing}
          suppressContentEditableWarning={true}
          className={`${styles.editableTitle} ${isEditing ? styles.editing : ""}`}
          title={!isEditing ? "Click to edit" : ""}
        >
          {collectionName}
        </h2>
        <p>
          {collection.total_apps === 0
            ? `${collection.total_apps} Apps`
            : `${collection.total_apps > 1 ? `${collection.total_apps} Apps` : `${collection.total_apps} App`}`}
        </p>
      </div>
    </div>
  );
};

export default CollectionDetailViewHeader;
